import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, Lightning, Repeat, Power } from "@phosphor-icons/react";

const DAYS = [
  { v: 0, label: "Mon", short: "Mo" },
  { v: 1, label: "Tue", short: "Tu" },
  { v: 2, label: "Wed", short: "We" },
  { v: 3, label: "Thu", short: "Th" },
  { v: 4, label: "Fri", short: "Fr" },
  { v: 5, label: "Sat", short: "Sa" },
  { v: 6, label: "Sun", short: "Su" },
];

const EMPTY = {
  name: "",
  guard_id: "",
  site_id: "",
  days_of_week: [],
  start_time_local: "08:00",
  end_time_local: "16:00",
  check_in_lead_minutes: 10,
};

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [guards, setGuards] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    try {
      const [t, g, s] = await Promise.all([
        api.get("/shift-templates"),
        api.get("/guards"),
        api.get("/sites"),
      ]);
      setTemplates(t.data);
      setGuards(g.data);
      setSites(s.data);
    } catch (e) {
      console.error("Templates.load failed", e);
    }
  };

  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      guard_id: t.guard_id,
      site_id: t.site_id,
      days_of_week: t.days_of_week,
      start_time_local: t.start_time_local,
      end_time_local: t.end_time_local,
      check_in_lead_minutes: t.check_in_lead_minutes,
    });
    setOpen(true);
  };

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort((a, b) => a - b),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (form.days_of_week.length === 0) { toast.error("Pick at least one day"); return; }
    try {
      if (editing) {
        await api.patch(`/shift-templates/${editing.template_id}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/shift-templates", form);
        toast.success("Template created");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (t) => {
    await api.patch(`/shift-templates/${t.template_id}`, { active: !t.active });
    await load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Existing shifts will remain.`)) return;
    await api.delete(`/shift-templates/${t.template_id}`);
    toast.success("Template deleted");
    await load();
  };

  const generate = async (t) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/shift-templates/${t.template_id}/generate?days=7`);
      toast.success(`Generated ${data.generated} shifts (${data.skipped} already existed)`);
    } catch {
      toast.error("Generate failed");
    } finally {
      setGenerating(false);
    }
  };

  const generateAll = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/shift-templates/generate-all?days=7");
      toast.success(`Generated ${data.generated} shifts across all active templates`);
    } catch {
      toast.error("Generate failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">// RECURRING</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">Shift Templates</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">
            Define a recurring schedule once and Sandy materializes real shifts for the next 7 days automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="generate-all-btn"
            onClick={generateAll}
            disabled={generating || templates.length === 0}
            className="flex items-center gap-2 border border-[#2BD4D9]/40 text-[#2BD4D9] hover:bg-[#2BD4D9]/10 px-3 py-2 text-xs uppercase tracking-wider disabled:opacity-40"
          >
            <Lightning size={14} weight="fill" /> Generate Next 7 Days
          </button>
          <button
            data-testid="add-template-btn"
            onClick={openCreate}
            className="flex items-center gap-2 bg-[#007AFF] hover:bg-[#0066d6] px-4 py-2.5 text-xs uppercase tracking-wider font-semibold"
          >
            <Plus size={14} weight="bold" /> Add Template
          </button>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="tac-card p-12 text-center">
          <Repeat size={32} className="mx-auto text-zinc-500 mb-3" weight="duotone" />
          <div className="label-mono mb-2">// NO TEMPLATES YET</div>
          <p className="text-sm text-zinc-400 max-w-md mx-auto">
            Create a template like &ldquo;Mon-Fri 6am-2pm at Downtown&rdquo; and Sandy will keep your dispatch board populated for the next week.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((t) => (
            <div key={t.template_id} className="tac-card p-5 group" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(t)}
                    data-testid={`toggle-active-${t.template_id}`}
                    title={t.active ? "Active" : "Paused"}
                    className={`w-7 h-7 grid place-items-center border ${t.active ? "border-emerald-500/40 text-emerald-400" : "border-zinc-700 text-zinc-600"} hover:bg-white/5`}
                  >
                    <Power size={14} weight={t.active ? "fill" : "regular"} />
                  </button>
                  <div className="label-mono text-[0.65rem]">{t.active ? "ACTIVE" : "PAUSED"}</div>
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <button data-testid={`edit-template-${t.template_id}`} onClick={() => openEdit(t)} className="border border-white/15 hover:bg-white/5 p-1.5"><PencilSimple size={12}/></button>
                  <button data-testid={`delete-template-${t.template_id}`} onClick={() => remove(t)} className="border border-white/15 hover:bg-red-500/10 hover:border-red-500/40 hover:text-red-400 p-1.5"><Trash size={12}/></button>
                </div>
              </div>
              <div className="font-semibold text-lg leading-tight">{t.name}</div>
              <div className="text-sm text-zinc-400 mt-1">{t.guard_name} @ {t.site_name}</div>
              <div className="flex gap-1 mt-3">
                {DAYS.map((d) => (
                  <span
                    key={d.v}
                    className={`w-9 h-7 grid place-items-center text-[0.65rem] font-mono uppercase ${t.days_of_week.includes(d.v) ? "bg-[#007AFF]/20 text-[#2BD4D9] border border-[#2BD4D9]/30" : "bg-white/[0.03] text-zinc-600 border border-white/5"}`}
                  >
                    {d.short}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-3 font-mono text-xs text-zinc-400">
                <span>{t.start_time_local} → {t.end_time_local}</span>
                <span className="text-zinc-700">·</span>
                <span>T-{t.check_in_lead_minutes}m ping</span>
              </div>
              <button
                onClick={() => generate(t)}
                disabled={generating || !t.active}
                data-testid={`generate-template-${t.template_id}`}
                className="w-full mt-4 flex items-center justify-center gap-2 border border-white/15 hover:border-[#2BD4D9] hover:text-[#2BD4D9] px-3 py-2 text-xs uppercase tracking-wider disabled:opacity-40"
              >
                <Lightning size={12} weight="fill" /> Generate Next 7 Days
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="template-modal">
          <div className="bg-[#121212] border border-white/10 max-w-lg w-full">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="label-mono">{editing ? "// EDIT TEMPLATE" : "// NEW TEMPLATE"}</div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-3">
              <div>
                <label className="label-mono block mb-1">Template Name</label>
                <input data-testid="template-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} placeholder="Downtown MWF Day" className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1">Guard</label>
                  <select data-testid="template-guard" required value={form.guard_id} onChange={(e)=>setForm({...form, guard_id:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm">
                    <option value="">Select guard</option>
                    {guards.map((g)=>(<option key={g.guard_id} value={g.guard_id}>{g.name}</option>))}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-1">Site</label>
                  <select data-testid="template-site" required value={form.site_id} onChange={(e)=>setForm({...form, site_id:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm">
                    <option value="">Select site</option>
                    {sites.map((s)=>(<option key={s.site_id} value={s.site_id}>{s.name}</option>))}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1">Days of Week</label>
                <div className="flex gap-1">
                  {DAYS.map((d) => (
                    <button
                      key={d.v}
                      type="button"
                      data-testid={`day-${d.v}`}
                      onClick={() => toggleDay(d.v)}
                      className={`flex-1 py-2 text-xs uppercase tracking-wider border ${form.days_of_week.includes(d.v) ? "bg-[#007AFF] border-[#007AFF] text-white" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label-mono block mb-1">Start</label>
                  <input data-testid="template-start" required type="time" value={form.start_time_local} onChange={(e)=>setForm({...form, start_time_local:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
                </div>
                <div>
                  <label className="label-mono block mb-1">End</label>
                  <input data-testid="template-end" required type="time" value={form.end_time_local} onChange={(e)=>setForm({...form, end_time_local:e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
                </div>
                <div>
                  <label className="label-mono block mb-1">Lead (min)</label>
                  <input data-testid="template-lead" type="number" min="0" max="120" value={form.check_in_lead_minutes} onChange={(e)=>setForm({...form, check_in_lead_minutes: parseInt(e.target.value,10) || 0})} className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"/>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
                <button data-testid="save-template-btn" type="submit" className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold">{editing ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Templates;
